import React, { useState, useEffect, useRef } from 'react';
import { Linkedin, ExternalLink, Users, ArrowRight } from 'lucide-react';

/* Scroll-reveal hook: triggers fade+slide-up when element enters viewport */
function useScrollReveal(options = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.unobserve(el);
        }
      },
      { threshold: 0.12, ...options }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

const INITIALS_MATRIX = [
  ['SC', 'JV', 'SS', 'DS'],
  ['MJ', 'OB', 'MJ', 'AM', 'AD'],
  ['RP', 'UJ', 'AM', 'HS'],
];

const INITIAL_TO_NAME = {
  SC: 'Dr. Shweta Choudhary',
  JV: 'Jeet Vijay',
  SS: 'Shivam Sharma',
  DS: 'Digvijay Singh',
  MJ: 'Manadar Joshi',
  OB: 'Onkar Bagaria',
  AM: 'Amit Singhal',
  AD: 'Anish Deshpande',
  RP: 'Raghushree Poddar',
  UJ: 'Umang Maheshwari',
  HS: 'Harshit Soni / Harsha Ramnani',
};

import { team } from '../../mock';
export const TEAM_MEMBERS = team;

function TeamCard({ member, isFeatured = false }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div className={`group relative rounded-2xl border border-white/10 bg-white/[0.02] hover:border-[#d42b92]/60 hover:bg-white/[0.04] transition-all duration-300 overflow-hidden shadow-lg hover:shadow-[#d42b92]/15 flex flex-col justify-between ${isFeatured ? 'h-auto lg:h-[505px]' : 'h-auto lg:h-[450px]'}`}>
      {/* Subtle ambient pink glow behind card on hover */}
      <div className="absolute -top-16 -right-16 w-36 h-36 rounded-full bg-[#d42b92]/0 group-hover:bg-[#d42b92]/20 blur-3xl transition-all duration-500 pointer-events-none" />

      {/* Top Section: Photo (ALWAYS Crisp & Unblurred) */}
      <div className={`relative w-full shrink-0 overflow-hidden bg-white/[0.03] ${isFeatured ? 'h-[240px] sm:h-[260px] lg:h-[285px]' : 'h-[210px] sm:h-[230px] lg:h-[250px]'}`}>
        {!imgError && member.image ? (
          <img
            src={member.image}
            alt={member.name}
            onError={() => setImgError(true)}
            className="w-full h-full object-cover object-top filter grayscale contrast-105 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#d42b92]/20 via-white/[0.02] to-black">
            <span className="text-4xl font-bold tracking-widest text-[#ff8ec5]">
              {member.fallbackInitials}
            </span>
          </div>
        )}

        {/* Subtle bottom gradient on photo to blend with card */}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#08080a] to-transparent pointer-events-none" />

        {/* Category Tag pill */}
        <div className="absolute top-3 left-3 z-10">
          <span className="px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-black/75 backdrop-blur-md border border-white/10 text-white/80 group-hover:border-[#d42b92]/50 group-hover:text-white transition-colors">
            {member.category}
          </span>
        </div>

        {/* Quick LinkedIn Link top right */}
        {member.linkedin && (
          <a
            href={member.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`View ${member.name} on LinkedIn`}
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/75 backdrop-blur-md border border-white/10 hover:border-[#0077b5] hover:bg-[#0077b5] text-white/70 hover:text-white flex items-center justify-center transition-all shadow-md"
          >
            <Linkedin className="w-3.5 h-3.5" />
          </a>
        )}
      </div>

      {/* Default Bottom Content */}
      <div className="p-4 sm:p-5 flex flex-col justify-between flex-1 transition-opacity duration-200 lg:group-hover:opacity-0">
        <div>
          <h3 className="text-base sm:text-lg font-bold text-white leading-snug">
            {member.name}
          </h3>
          <p className="mt-1 text-xs sm:text-[13px] font-medium text-[#ff8ec5] leading-relaxed line-clamp-2">
            {member.designation}
          </p>
          {member.intro && (
            <p className="mt-2.5 text-xs text-white/70 leading-relaxed font-normal line-clamp-3 lg:hidden">
              {member.intro}
            </p>
          )}
        </div>

        {/* Desktop-only hover cue */}
        <div className="hidden lg:flex pt-3 border-t border-white/10 items-center justify-end text-xs text-white/40">
          <span className="text-[11px] text-white/50 flex items-center gap-1 group-hover:text-[#ff8ec5]">
            <span>Hover for details</span>
            <span>&rarr;</span>
          </span>
        </div>
      </div>

      {/* On-Hover Slide-up Drawer: Desktop only */}
      <div className="hidden lg:flex absolute inset-x-0 bottom-0 bg-[#0d0d12]/98 border-t border-[#d42b92]/40 backdrop-blur-md p-5 flex-col justify-between translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out z-20 shadow-2xl">
        <div>
          <div className="mb-1.5">
            <h3 className="text-base font-bold text-white leading-snug">
              {member.name}
            </h3>
          </div>

          <p className="text-xs font-semibold text-[#ff8ec5] leading-tight mb-2.5">
            {member.designation}
          </p>

          <p className="text-xs text-white/80 leading-relaxed font-normal line-clamp-4">
            {member.intro}
          </p>
        </div>

        {member.linkedin && (
          <a
            href={member.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3.5 inline-flex items-center justify-center gap-2 w-full py-2 px-3 rounded-xl bg-white/[0.06] hover:bg-[#d42b92] border border-white/15 hover:border-[#ff8ec5]/60 text-white text-xs font-semibold tracking-wide transition-all shadow-md group/btn"
          >
            <Linkedin className="w-3.5 h-3.5 fill-current" />
            <span>View LinkedIn Profile</span>
            <ExternalLink className="w-3 h-3 opacity-60 group-hover/btn:opacity-100 transition-opacity" />
          </a>
        )}
      </div>
    </div>
  );
}

/* Wrapper that applies staggered scroll-reveal to each card */
function ScrollRevealCard({ children, index, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Start invisible/shifted
    el.style.opacity = '0';
    el.style.transform = 'translateY(36px)';
    el.style.transition = `opacity 0.55s ease ${index * 80}ms, transform 0.55s ease ${index * 80}ms`;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [index]);
  return <div ref={ref} className={className}>{children}</div>;
}

export default function TheTeamSection() {
  const [activeTab, setActiveTab] = useState('All');

  const categories = ['All', 'Leadership', 'Core Team', 'Strategic Partner'];

  const leadershipMembers = TEAM_MEMBERS.filter((m) => m.category === 'Leadership');
  const coreTeamMembers = TEAM_MEMBERS.filter((m) => m.category === 'Core Team');
  const strategicPartners = TEAM_MEMBERS.filter((m) => m.category === 'Strategic Partner');

  const shweta = leadershipMembers.find((m) => m.id === 'shweta-choudhary');
  const digvijay = leadershipMembers.find((m) => m.id === 'digvijay-singh');
  const omkar = leadershipMembers.find((m) => m.id === 'onkar-bagaria');
  const jeet = leadershipMembers.find((m) => m.id === 'jeet-vijay');
  const shivam = leadershipMembers.find((m) => m.id === 'shivam-sharma');

  const showLeadership = activeTab === 'All' || activeTab === 'Leadership';
  const showCoreTeam = activeTab === 'All' || activeTab === 'Core Team';
  const showStrategicPartners = activeTab === 'All' || activeTab === 'Strategic Partner';

  return (
    <section id="the-team-section" className="relative bg-[#08080a] text-white py-20 lg:py-28 border-t border-white/5 overflow-hidden scroll-mt-24">
      {/* Ambient pink backlights */}
      <div className="absolute top-1/3 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[650px] h-[650px] rounded-full bg-[#d42b92]/10 blur-[180px] pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-[500px] h-[500px] rounded-full bg-[#d42b92]/8 blur-[160px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Narrative & Header Area */}
        <div className="max-w-4xl reveal-up">
          {/* Heading */}
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.08]">
            Experience that moves <span className="text-[#d42b92]">ventures forward.</span>
          </h2>

          {/* Narrative Paragraphs */}
          <div className="mt-8 space-y-5 text-white/80 text-base sm:text-lg leading-relaxed font-normal">
            <p>
              At the heart of InnovHer is a growing network of experts who have built, led, invested, created and learned across industries.
            </p>
            <p>
              The core team works alongside founders through the everyday realities of building, supported by mentors, advisors, investors, domain experts and business leaders who bring specialized experience when it matters.
            </p>
            <p>
              Rather than creating a large passive network, InnovHer is designed around meaningful access, connecting ventures to relevant expertise, perspectives and relationships at different stages of their journey.
            </p>
            <p className="text-white/90 font-medium">
              Because the strongest ecosystems are not defined by how many people they know, but by how effectively the right people come together to create progress.
            </p>
          </div>
        </div>

        {/* Category Filters Bar */}
        <div className="mt-14 flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-5 reveal-up">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#ff8ec5]" />
            <span className="text-sm font-semibold text-white tracking-wide">
              Team Directory
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {categories.map((cat) => {
              const active = activeTab === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveTab(cat)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wider transition-all ${active
                    ? 'bg-[#d42b92] text-white shadow-md shadow-[#d42b92]/20'
                    : 'border border-white/10 bg-white/[0.02] text-white/60 hover:text-white hover:border-white/20'
                    }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>

        {/* Three Clearly Separated Sections */}
        <div className="mt-12 lg:mt-16 space-y-16 lg:space-y-20">
          {/* Section 1: Leadership */}
          {showLeadership && (
            <section aria-labelledby="heading-leadership" className="space-y-10 lg:space-y-12">
              <div className="flex items-center gap-4">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                <h3 id="heading-leadership" className="text-sm sm:text-base font-bold tracking-[0.2em] uppercase text-white/70 text-center flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#d42b92] inline-block" />
                  Leadership
                </h3>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>

              {/* Level 1: Founder & Director (Centered with balanced prominence) */}
              {shweta && (
                <div className="flex flex-col items-center">
                  <div className="mb-4">
                    <span className="text-[11px] font-semibold tracking-[0.25em] text-[#ff8ec5] uppercase px-3 py-1 rounded-full bg-[#d42b92]/10 border border-[#d42b92]/20">
                      Founder &amp; Vision
                    </span>
                  </div>
                  <ScrollRevealCard index={0} className="w-full max-w-[335px] lg:max-w-[345px]">
                    <TeamCard member={shweta} isFeatured />
                  </ScrollRevealCard>

                  {/* Connecting line to Pillar Leadership */}
                  <div className="w-px h-8 bg-gradient-to-b from-[#d42b92]/60 via-white/20 to-transparent mt-6 hidden lg:block" />
                </div>
              )}

              {/* Level 2: Executive Leadership & Pillar Leads */}
              <div>
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                  <span className="text-xs font-semibold tracking-[0.2em] uppercase text-white/50 text-center">
                    Pillar Leadership &amp; Strategic Directors
                  </span>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-8 items-stretch">
                  {[digvijay, omkar, jeet, shivam].filter(Boolean).map((member, i) => (
                    <ScrollRevealCard key={member.id} index={i + 1} className="w-full h-full">
                      <TeamCard member={member} />
                    </ScrollRevealCard>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Section 2: Core Team */}
          {showCoreTeam && (
            <section aria-labelledby="heading-core-team" className="space-y-8">
              <div className="flex items-center gap-4">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                <h3 id="heading-core-team" className="text-sm sm:text-base font-bold tracking-[0.2em] uppercase text-white/70 text-center flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#d42b92] inline-block" />
                  Core Team
                </h3>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>

              {/* Balanced 2-card centered layout */}
              <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 lg:gap-8 items-stretch">
                {coreTeamMembers.map((member, i) => (
                  <ScrollRevealCard key={member.id} index={i + 5} className="w-full h-full">
                    <TeamCard member={member} />
                  </ScrollRevealCard>
                ))}
              </div>
            </section>
          )}

          {/* Section 3: Strategic Partners */}
          {showStrategicPartners && (
            <section aria-labelledby="heading-strategic-partners" className="space-y-8">
              <div className="flex items-center gap-4">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                <h3 id="heading-strategic-partners" className="text-sm sm:text-base font-bold tracking-[0.2em] uppercase text-white/70 text-center flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#d42b92] inline-block" />
                  Strategic Partners
                </h3>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-8 items-stretch">
                {strategicPartners.map((member, i) => (
                  <ScrollRevealCard key={member.id} index={i + 7} className="w-full h-full">
                    <TeamCard member={member} />
                  </ScrollRevealCard>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Bottom Banner Note */}
        <div className="mt-14 rounded-2xl border border-white/10 bg-gradient-to-r from-[#d42b92]/10 via-white/[0.02] to-transparent p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 reveal-up">
          <div>
            <h4 className="text-white font-bold text-base sm:text-lg">
              Want to collaborate or join our expert mentor &amp; advisor network?
            </h4>
            <p className="mt-1 text-xs sm:text-sm text-white/70 max-w-2xl">
              Connecting ventures to relevant expertise, perspectives and relationships at different stages of their journey.
            </p>
          </div>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#d42b92] hover:bg-[#e0339f] text-white text-xs sm:text-sm font-semibold tracking-wide transition-colors whitespace-nowrap shadow-lg shadow-[#d42b92]/20"
          >
            <span>Connect with Team</span>
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
